import { type CSSProperties, useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import classNames from "classnames"

import { useLazyGetTableColumnsQuery } from "store/api/dashboardApi"
import { useLazyGetEnterpriseThermostatsQuery } from "store/api/enterpriseApi"
import { useGetEnterpriseDashboardThermostatWidgetsQuery } from "store/api/widgetsApi"
import { useAppDispatch } from "store/hooks"
import { setSelectedDeviceIDs, setSelectedDevices } from "store/tempDataSlise"
import { ApiTypes, UITypes } from "types"

import { parseDateField } from "utils/dateUtils"
import getErrorMessage from "utils/getErrorMessage"
import { reorderTableHeaderCells } from "utils/reorderTabbleColumns"
import useExportCSV from "hooks/useExportCSV"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { WidgetLayout } from "layouts/WidgetLayout"
import { ContentWrapper } from "components/common/ContentWrapper"
import { Pagination } from "components/common/Pagination"
import { RainbowChart } from "components/common/RainbowChart"
import EditColumnsButton from "components/EditColumnsButton"
import { ButtonNew } from "components/form/ButtonNew"
import TableWrapper from "components/table/TableWrapper"
import { WidgetPhysicalStatus } from "components/widgets/WidgetPhysicalStatus"

import { enterpriseDevicesTableHeaderCells } from "./enterpriseDevicesTableHeaderCells"

import styles from "./index.module.scss"

const TABLE_NAME = "admin_devices_list"
const THERMOSTAT_STATUS_FILTERS = [
  { label: "Missing", value: "missing", className: styles.missing },
  { label: "Transfer", value: "transferring", className: styles.transfer },
  { label: "Warranty", value: "warranty", className: styles.warranty },
  { label: "In Inventory", value: "inventory", className: styles.inventory },
  { label: "Pre-Installed", value: "preinstalled", className: styles.preinstalled },
] as const

const formatSystemAge = (age?: number | null) => {
  if (age === undefined || age === null) return ""
  if (age < 3) return "Less than 3 years"
  if (age >= 20) return "More than 20 years"
  if (age >= 15) return "More than 15 & Less than 20 years"
  if (age >= 10) return "More than 10 & Less than 15 years"
  if (age >= 8) return "More than 8 & Less than 10 years"
  if (age >= 5) return "More than 5 years"
  return "More than 3 years"
}

const InventoryWidget = ({ widget }: { widget?: ApiTypes.Res.InventoryWidget }) => {
  const inventory = widget?.inventory_devices_v1 ?? 0
  const ordered = widget?.ordered_devices ?? 0
  const total = inventory + ordered
  const inventoryPercent = total > 0 ? `${(inventory / total) * 100}%` : "0%"

  return (
    <WidgetLayout title="Inventory" className={classNames(styles.summaryWidget)}>
      <div className={styles.summaryWidgetInner}>
        <div className={styles.inventoryBarWrap}>
          <div className={styles.inventoryBar}>
            <span className={styles.inventoryBarInStock} style={{ flexBasis: inventoryPercent }} />
            <span
              className={styles.inventoryBarOrdered}
              style={{ flexBasis: total > 0 ? `${(ordered / total) * 100}%` : "0%" }}
            />
          </div>
          <div className={styles.inventorySufficientFor}>
            <span>Sufficient for</span>
            <div className={styles.inventoryDays}>
              {widget?.sufficient_days ?? 0}
              <small>Days</small>
            </div>
          </div>
        </div>
        <div className={styles.inventoryLegendRow}>
          <div className={styles.inventoryLegendItem}>
            <span className={styles.inventoryLegendLabel} style={{ "--legend-color": "#3b82f6" } as CSSProperties}>
              In Inventory
            </span>
            <strong>{inventory}</strong>
          </div>
          <div className={styles.inventoryLegendItem}>
            <span className={styles.inventoryLegendLabel} style={{ "--legend-color": "#dbeafe" } as CSSProperties}>
              Ordered
            </span>
            <strong>{ordered}</strong>
          </div>
        </div>
      </div>
    </WidgetLayout>
  )
}

const MonitoringWidget = ({ widget }: { widget?: ApiTypes.Res.MonitoringWidget }) => {
  const purchased = widget?.purchased_devices ?? 0
  const installed = widget?.installed_devices ?? 0
  const remaining = Math.max(purchased - installed, 0)
  const percent = purchased > 0 ? Math.round((installed / purchased) * 100) : 0

  return (
    <WidgetLayout title="Monitoring" className={classNames(styles.summaryWidget)}>
      <div className={styles.monitoringChartWrap}>
        <RainbowChart
          values={[installed, remaining]}
          colors={["#48d7f3", "#e2e8f0"]}
          options={{ cutout: "68%", rotation: -90, circumference: 180 }}
        />
        <div className={styles.monitoringMainValue}>
          <span>INSTALLED</span>
          <strong>{percent}%</strong>
        </div>
      </div>
      <div className={styles.inventoryLegendRow}>
        <div className={styles.inventoryLegendItem}>
          <span className={styles.inventoryLegendLabel} style={{ "--legend-color": "#48d7f3" } as CSSProperties}>
            Purchased
          </span>
          <strong>{purchased}</strong>
        </div>
        <div className={styles.inventoryLegendItem}>
          <span className={styles.inventoryLegendLabel} style={{ "--legend-color": "#e2e8f0" } as CSSProperties}>
            Installed
          </span>
          <strong>{installed}</strong>
        </div>
      </div>
    </WidgetLayout>
  )
}

const convertEnterpriseDevicesToTableRows = (data?: ApiTypes.Model.EnterpriseThermostat[]): UITypes.TableBodyRow[] => {
  if (!data?.length) return []

  return data.map(
    device =>
      ({
        checkbox: {},
        sn: { value: device.sn },
        work_status: { value: device.work_status },
        contractor_name: { value: device.contractor },
        phisical_status: { value: device.physical_status },
        contractor: { value: device.contractor_id },
        activation_date: { value: parseDateField(device.activation_date) },
        red_flag: { value: `${device.red_flag ? "Yes" : "No"}` },
        red_flag_type: { value: device.red_flag_type },
        homeowner: { value: device.homeowner },
        email: { value: device.email },
        country: { value: device.country_name },
        zip_code: { value: device.zip_code },
        address: { value: device.address },
        phone: { value: device.phone },
        system_age: { value: formatSystemAge(device.system_age), csvValue: formatSystemAge(device.system_age) },
        device_location: { value: device.device_location },
        rowSettings: {
          id: Number(device.id),
          isRedFlag: device.red_flag,
          item: {
            ...device,
            phisical_status: device.physical_status,
            contractor_name: device.contractor,
          },
        },
      }) as UITypes.TableBodyRow,
  )
}

const convertEnterpriseThermostatToSelectedDevice = (
  device: ApiTypes.Model.EnterpriseThermostat,
): ApiTypes.Model.VendorDevice => ({
  id: device.id,
  sn: device.sn,
  work_status: device.work_status,
  phisical_status: device.physical_status,
  contractor: device.contractor_id,
  contractor_name: device.contractor,
  activation_date: device.activation_date,
  red_flag: device.red_flag,
  red_flag_type: device.red_flag_type ?? "",
  homeowner: device.homeowner,
  country_name: device.country_name,
  address: device.address,
  city: device.city,
  state: device.state,
  email: device.email,
  phone: device.phone,
  system_age: device.system_age ?? 0,
  device_location: device.device_location ?? "",
  hvac: undefined,
  installation_type: device.installation_type,
  zip_code: device.zip_code,
  system_type: device.system_type,
  accessories: device.accessories,
  time_zone_text: device.time_zone_text,
})

export const EnterpriseDevicesPage = () => {
  const dispatch = useAppDispatch()
  const [triggerGetDevices, { isFetching, data }] = useLazyGetEnterpriseThermostatsQuery()
  const [triggerGetTableColumns, { data: columnsData, isFetching: isColumnsFetching }] = useLazyGetTableColumnsQuery()
  const { data: deviceWidgetsResponse } = useGetEnterpriseDashboardThermostatWidgetsQuery()
  const { urlQueryParams, setUrlQueryParams } = useUrlQueryParams()
  const [selectedDeviceIDs, setSelectedDeviceIDsLocal] = useState<number[]>([])
  const devices = data?.data ?? []
  const deviceWidgets = deviceWidgetsResponse?.data
  const activePhysicalStatusFilter = urlQueryParams.phisical_status ?? null

  const tableHeaderCells = useMemo(
    (): UITypes.TableHeaderCell[] =>
      columnsData?.data?.config
        ? reorderTableHeaderCells(enterpriseDevicesTableHeaderCells, columnsData.data.config)
        : enterpriseDevicesTableHeaderCells,
    [columnsData],
  )

  const tableRows = useMemo(() => convertEnterpriseDevicesToTableRows(devices), [devices])
  const { handleExport } = useExportCSV({
    params: urlQueryParams,
    headerCells: tableHeaderCells,
    meta: data?.meta,
    fileName: "enterprise-devices",
    convertData: convertEnterpriseDevicesToTableRows,
    useLazyQuery: useLazyGetEnterpriseThermostatsQuery,
  })

  const selectedPageDevices = useMemo(
    () => devices.filter(device => selectedDeviceIDs.includes(device.id)),
    [devices, selectedDeviceIDs],
  )
  const isHeaderChecked = devices.length > 0 && devices.every(device => selectedDeviceIDs.includes(device.id))

  const onDeviceSelect = (id: number) => {
    setSelectedDeviceIDsLocal(current =>
      current.includes(id) ? current.filter(item => item !== id) : [...current, id],
    )
  }

  const toggleAllDevices = () => {
    if (isHeaderChecked) {
      setSelectedDeviceIDsLocal(current => current.filter(id => !devices.map(device => device.id).includes(id)))
      return
    }

    setSelectedDeviceIDsLocal(current => Array.from(new Set([...current, ...devices.map(device => device.id)])))
  }

  const onStatusPillClick = (status: string) => {
    const nextStatus = activePhysicalStatusFilter === status ? null : status

    setUrlQueryParams({
      phisical_status: nextStatus,
      page: 1,
    })
  }

  const sendRequest = async (params: UITypes.Params) => {
    try {
      const res = await triggerGetDevices(params)
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      }
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    sendRequest(urlQueryParams)
    triggerGetTableColumns({ list_name: TABLE_NAME })
  }, [triggerGetDevices, triggerGetTableColumns, urlQueryParams])

  useEffect(
    () => () => {
      dispatch(setSelectedDeviceIDs([]))
      dispatch(setSelectedDevices([]))
    },
    [dispatch],
  )

  useEffect(() => {
    const result = devices
      .filter(device => selectedDeviceIDs.some(id => id === device.id))
      .map(convertEnterpriseThermostatToSelectedDevice)
    dispatch(setSelectedDevices(result))
  }, [devices, dispatch, selectedDeviceIDs])

  return (
    <>
      <section className={styles.widgetsSection}>
        <div className={styles.widgetsWrapper}>
          <WidgetPhysicalStatus variant="enterprise" />
          <InventoryWidget widget={deviceWidgets?.inventory} />
          <MonitoringWidget widget={deviceWidgets?.monitoring} />
        </div>
      </section>
      <ContentWrapper title="Thermostat">
        <nav className={styles.tableNavWrapper}>
          <div className={styles.statusPills}>
            {THERMOSTAT_STATUS_FILTERS.map(status => (
              <button
                key={status.value}
                type="button"
                className={classNames(styles.statusPill, status.className, {
                  [styles.statusPillActive]: activePhysicalStatusFilter === status.value,
                })}
                aria-pressed={activePhysicalStatusFilter === status.value}
                onClick={() => onStatusPillClick(status.value)}
              >
                {status.label}
              </button>
            ))}
          </div>
          <div className={styles.tableActions}>
            <EditColumnsButton tableName={TABLE_NAME} tableHeaderCells={tableHeaderCells} />
            <ButtonNew onClick={handleExport} disabled={data?.meta?.total === 0}>
              Export
            </ButtonNew>
          </div>
        </nav>
        <TableWrapper
          headerCells={tableHeaderCells}
          columnsLoaded={!isColumnsFetching}
          isLoading={isFetching}
          currentItemIDs={selectedDeviceIDs}
          onCheckboxChange={onDeviceSelect}
          data={tableRows}
          onHeaderCheckboxChange={toggleAllDevices}
          isHeaderChecked={isHeaderChecked}
          clearAdditionalTags={() => setSelectedDeviceIDsLocal([])}
          additionalTags={
            selectedPageDevices.length
              ? [
                  {
                    label: "S/N",
                    values: selectedPageDevices.map(item => item.sn),
                  },
                ]
              : []
          }
        />
        <Pagination meta={data?.meta} />
      </ContentWrapper>
    </>
  )
}

export default EnterpriseDevicesPage
